import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import Big from 'big.js'
import { useTranslation } from 'react-i18next'

import { GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { ProductKind, STAGING_PRODUCT_KINDS } from 'constants/product'
import { CounterInput } from 'components/common/CounterInput'
import { SystemMessage } from 'components/common/SystemMessage'
import { GALLERY_DRAWER_HEADER_SX, ResponsiveDrawer } from 'components/common/ResponsiveDrawer'
import type { SalesforceId } from 'models/purchaseFlowRevamp'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'

import { PfRevampEventName, usePfRevampAnalytics } from '../../analytics'
import { PropertySelectionDrawer } from '../../entry/PropertySelectionDrawer.component'
import { STAGING_SENTINEL_PRODUCT_ID } from '../../fileHub'
import { ProductGroupIcon } from '../../steps/catalogue/ProductGroupIcon.component'
import { EMPTY_PACK_MEMBER_ADDONS, usePfStore } from '../../store'
import { allowsMultipleDocuments, isDocumentShell, variantsDisplacedBySelectionGroup } from '../../store/documentSelectionPolicy'
import { isPack } from '../../store/packLines'
import { displacedCartItems } from '../../store/quotaPreview'
import { drawerShootOutputUse } from '../../store/shootOutput'
import { AddonLists } from './AddonLists.component'
import { BundleContentsSection } from './BundleContentsSection.component'
import { DrawerFooter } from './DrawerFooter.component'
import { DrawerSection } from './DrawerSection.component'
import { FileUploadSection } from './FileUploadSection.component'
import { PackMembersSection } from './PackMembersSection.component'
import { type PackMemberQuote, quotePackMember } from './packMemberQuote'
import { productCommentSurface } from './productComment'
import { ProductCommentSection } from './ProductCommentSection.component'
import { useDrawerAddons } from './useDrawerAddons'
import { drawerDescription } from './drawerDescription'
import { useDrawerCommit } from './useDrawerCommit'
import { useDrawerPricing } from './useDrawerPricing'
import { useDrawerUploads } from './useDrawerUploads'
import { VariantPicker } from './VariantPicker.component'
import { PfDrawerScrim } from '../PfDrawerScrim.component'

/** Stable empty list — a fresh `[]` would re-run the pricing memo every render. */
const EMPTY_VARIANT_IDS: readonly SalesforceId[] = []

/**
 * The product drawer: choose a variant, its options, uploads and extras, then add the line to the
 * cart (`new`, opened from a card) or rewrite it (`edit`, opened from a cart line). Pending choices
 * live in `productDrawerSlice`; the cart only ever sees committed lines.
 *
 * Sections, top to bottom: description, variant, bundle contents and the product's own options on
 * white; then the upload, the client's brief and the additional products, the last of which fills the
 * rest of the drawer in beige.
 */
export const ProductDrawer: React.FC = () => {
  const { t } = useTranslation(['purchase_flow_revamp', 'order'])

  const isOpen = usePfStore((s) => s.isOpen)
  const mode = usePfStore((s) => s.mode)
  const shellProductId = usePfStore((s) => s.shellProductId)
  const selectedVariantId = usePfStore((s) => s.selectedVariantId)
  const additionalVariantIds = usePfStore((s) => s.additionalVariantIds)
  const optionValues = usePfStore((s) => s.optionValues)
  const selectedExtraIds = usePfStore((s) => s.selectedExtraIds)
  const extraQuantities = usePfStore((s) => s.extraQuantities)
  const quantity = usePfStore((s) => s.quantity)
  const setQuantity = usePfStore((s) => s.setQuantity)
  const editingCartItemId = usePfStore((s) => s.editingCartItemId)
  const note = usePfStore((s) => s.note)
  const cartItems = usePfStore((s) => s.items)
  const property = usePfStore((s) => s.property)
  const closeDrawer = usePfStore((s) => s.close)
  const setVariant = usePfStore((s) => s.setVariant)
  const toggleDocumentVariant = usePfStore((s) => s.toggleDocumentVariant)
  const setNote = usePfStore((s) => s.setNote)
  const unselectedPackMemberCodes = usePfStore((s) => s.unselectedPackMemberCodes)
  const packMemberAddons = usePfStore((s) => s.packMemberAddons)
  const togglePackMember = usePfStore((s) => s.togglePackMember)
  const trackAnalytics = usePfRevampAnalytics()

  // A new line needs a property first; an edited one already has it.
  const noPropertySelected = !property && mode === 'new'
  const [isPropertySelectionOpen, setIsPropertySelectionOpen] = useState(false)

  const catalogue = usePfCatalogue().data
  const shell = useMemo(
    () => (catalogue && shellProductId ? catalogue.shellProducts.find((p) => p.id === shellProductId) ?? null : null),
    [catalogue, shellProductId]
  )
  const selectedVariant = shell?.variants.find((variant) => variant.id === selectedVariantId)
  const groupDescription = drawerDescription(shell)
  /** Made from the shoot already in the cart: no upload, only how many images (staging) and a note on what happens next. */
  const shootUse = useMemo(
    () => drawerShootOutputUse(shell, cartItems, catalogue?.shellProducts, editingCartItemId),
    [shell, cartItems, catalogue, editingCartItemId]
  )

  /** Authorities-document groups let the client tick several documents, each its own line (`documentSelectionPolicy`). */
  const isMultiSelectDocuments = useMemo(() => mode === 'new' && allowsMultipleDocuments(shell), [mode, shell])

  const tickedVariantIds = useMemo<SalesforceId[]>(
    () => (selectedVariantId ? [selectedVariantId, ...additionalVariantIds] : []),
    [selectedVariantId, additionalVariantIds]
  )

  const handleVariantSelect = useCallback((variantId: string) => {
    if (isMultiSelectDocuments) {
      // Displacement runs on selection only: unticking a document never removes its sibling.
      const displaced = tickedVariantIds.includes(variantId)
        ? []
        : variantsDisplacedBySelectionGroup(variantId, shell?.variants ?? [], tickedVariantIds)
      toggleDocumentVariant(variantId, displaced)
    } else {
      setVariant(variantId)
    }

    if (shellProductId) {
      trackAnalytics({
        type: PfRevampEventName.DRAWER_VARIANT_SELECTED,
        payload: { shellProductId, variantId },
      })
    }
  }, [isMultiSelectDocuments, tickedVariantIds, shell, toggleDocumentVariant, setVariant, trackAnalytics, shellProductId])

  const pricing = useDrawerPricing(
    catalogue,
    shellProductId,
    selectedVariantId,
    optionValues,
    selectedExtraIds,
    quantity,
    extraQuantities,
    // Outside multi-select the store may still hold stale ids.
    isMultiSelectDocuments ? additionalVariantIds : EMPTY_VARIANT_IDS
  )

  /** A pack's services priced with their add-ons; null for anything that is not a pack. */
  const packQuotes = useMemo(() => {
    if (!isPack(selectedVariant) || !selectedVariant) return null
    return (selectedVariant.bundledProducts ?? [])
      .map((member) => quotePackMember(catalogue, member, packMemberAddons[member.productCode] ?? EMPTY_PACK_MEMBER_ADDONS))
      .filter((quote): quote is PackMemberQuote => quote !== null)
  }, [selectedVariant, catalogue, packMemberAddons])

  /** What the ticked services cost: those are the lines the cart will hold. */
  const packTicked = useMemo(
    () => packQuotes?.filter((quote) => !unselectedPackMemberCodes.includes(quote.member.productCode)) ?? null,
    [packQuotes, unselectedPackMemberCodes]
  )
  const packTotal = useMemo(
    () => packTicked?.reduce((sum, quote) => sum.plus(quote.total ?? 0), new Big(0)) ?? null,
    [packTicked]
  )

  const quotaNote = useMemo(() => {
    if (mode !== 'new') return undefined
    const displaced = displacedCartItems(pricing.variant, cartItems)
    if (displaced.length === 0) return undefined
    const variants = catalogue?.shellProducts.flatMap((candidate) => candidate.variants) ?? []
    const names = displaced
      .map((item) => variants.find((candidate) => candidate.id === item.variantId)?.displayName)
      .filter((name): name is string => Boolean(name))
    return names.length > 0 ? t('purchase_flow_revamp:drawer.quota_displaces', { products: names.join(', ') }) : undefined
  }, [mode, pricing.variant, cartItems, catalogue, t])

  const requiredOptionsMissing = useMemo(
    () => pricing.options
      .filter((opt) => opt.required)
      .some((opt) => optionValues[opt.id] === undefined || optionValues[opt.id] === ''),
    [pricing.options, optionValues]
  )

  const uploads = useDrawerUploads(shell, selectedVariant)
  const addons = useDrawerAddons(pricing, shell)

  // The brief follows the visuals it describes, so a product with a configuration page takes it there.
  const commentSurface = productCommentSurface({
    categoryId: shell?.categoryId,
    kind: selectedVariant?.kind ?? shell?.kind,
    configurationFlows: shell?.configurationFlows,
  })

  const handleCommit = useDrawerCommit({
    shell,
    selectedVariant,
    pricing,
    uploads,
    commentSurface,
    isMultiSelectDocuments,
    tickedVariantIds,
    catalogue,
  })

  const showProductOptions = addons.productOptions.length > 0 || addons.productExtras.length > 0
  const showExtrasSection = addons.standaloneOptions.length > 0 || addons.standaloneExtras.length > 0
  const showCommentSection = commentSurface === 'drawer'
  const isCommentSectionLast = showCommentSection && !showExtrasSection
  const isConfigSectionLast = uploads.showDrawerUpload && !showExtrasSection && !showCommentSection

  // Lock the catalogue's scroll while the drawer is open.
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // A route change mid-configuration must not leave the drawer open.
  useEffect(() => () => {
    if (usePfStore.getState().isOpen) usePfStore.getState().close()
  }, [])

  return (
    <>
      {isOpen &&
        <PfDrawerScrim onClick={closeDrawer} />
      }

      {/* Layered above the product drawer, which stays mounted underneath; picking a property closes
          only this one, and the footer then offers the commit. */}
      <PropertySelectionDrawer
        isOpen={isPropertySelectionOpen}
        onClose={() => setIsPropertySelectionOpen(false)}
        onProceed={() => setIsPropertySelectionOpen(false)}
        allowStacking
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 10 }}
      />

      <ResponsiveDrawer
        isOpen={isOpen}
        onClose={closeDrawer}
        anchor="right"
        width="48rem"
        headerSx={GALLERY_DRAWER_HEADER_SX}
        contentSx={{ p: 0 }}
        title={
          <Stack direction="row" alignItems="center" gap={1.2} minWidth={0}>
            <ProductGroupIcon productGroupCode={shell?.productGroupCode} size="lg" background="white" fallback={null} />
            <Typography variant="text-lg" fontWeight={600} color={GRAY_900}>
              {shell?.displayName ?? t('purchase_flow_revamp:drawer.loading')}
            </Typography>
          </Stack>
        }
      >
        <Stack sx={{ height: '100%', backgroundColor: WHITE }}>

          <Stack sx={{ flex: 1, overflowY: 'auto' }}>

            <Stack gap={2.4} sx={{ backgroundColor: WHITE, p: 2.4 }}>

              {groupDescription &&
                <Typography variant="text-sm" color={GRAY_700} sx={{ lineHeight: 1.6 }}>
                  {groupDescription}
                </Typography>
              }

              {shell && !packQuotes &&
                <VariantPicker
                  variants={shell.variants}
                  selectedVariantId={selectedVariantId}
                  selectedVariantIds={tickedVariantIds}
                  multiSelect={isMultiSelectDocuments}
                  documentStyle={isDocumentShell(shell)}
                  onSelect={handleVariantSelect}
                  sectionLabel={shell.variantSectionLabel}
                  // A line already in the cart keeps its product; what is edited hangs off it.
                  locked={mode === 'edit'}
                />
              }

              {shell?.note && <SystemMessage variant="info" message={shell.note} />}

              {isPack(selectedVariant)
                ? <PackMembersSection
                  quotes={packQuotes ?? []}
                  unselectedCodes={unselectedPackMemberCodes}
                  onToggle={togglePackMember}
                />
                : <BundleContentsSection members={selectedVariant?.bundledProducts ?? []} />
              }

              {/* Untitled: these are part of the chosen product, not a set of something else. */}
              {showProductOptions &&
                <Stack gap={1.2}>
                  <AddonLists options={addons.productOptions} extras={addons.productExtras} handlers={addons} />
                </Stack>
              }

              {shootUse &&
                <Stack gap={0.8} data-cy="pf-revamp-drawer-from-shoot">
                  {shootUse === 'staging' &&
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.6}>
                      <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>
                        {t('purchase_flow_revamp:drawer.from_shoot.staging_quantity')}
                      </Typography>
                      <CounterInput value={quantity} minValue={1} onChange={setQuantity} />
                    </Stack>
                  }
                  <Typography variant="text-sm" color={GRAY_700}>
                    {t(`purchase_flow_revamp:drawer.from_shoot.${shootUse}`)}
                  </Typography>
                </Stack>
              }

            </Stack>

            {uploads.showDrawerUpload && !shootUse &&
              <DrawerSection
                title={t('purchase_flow_revamp:drawer.config_label')}
                subtitle={t('purchase_flow_revamp:drawer.config_upload_label')}
                isLast={isConfigSectionLast}
                divided
                gap={1.6}
              >
                <FileUploadSection
                  uploadProductId={STAGING_SENTINEL_PRODUCT_ID}
                  productCode={selectedVariantId}
                  scopeKey={shellProductId}
                  claimedByOtherLines={uploads.claimedByOtherLines}
                  checksImageQuality={STAGING_PRODUCT_KINDS.has(shell?.kind as ProductKind)}
                />
              </DrawerSection>
            }

            {showCommentSection &&
              <ProductCommentSection value={note} onChange={setNote} isLast={isCommentSectionLast} />
            }

            {showExtrasSection &&
              <DrawerSection
                title={t('purchase_flow_revamp:drawer.options_label')}
                isLast
              >
                <AddonLists options={addons.standaloneOptions} extras={addons.standaloneExtras} handlers={addons} />
              </DrawerSection>
            }

          </Stack>

          <DrawerFooter
            mode={mode}
            lineTotal={packTotal ?? pricing.lineTotal}
            baseLineTotal={packTotal ? null : pricing.baseLineTotal}
            subtotalLabel={packTotal ? t('purchase_flow_revamp:drawer.bundle_subtotal_label') : undefined}
            // Nothing is unavailable yet: no document is picked.
            unpricedLabel={isDocumentShell(shell) && tickedVariantIds.length === 0 ? t('purchase_flow_revamp:drawer.select_document') : undefined}
            currency={pricing.currency}
            isPriced={pricing.isPriced}
            onCommit={handleCommit}
            isCommitDisabled={requiredOptionsMissing || uploads.uploadsMissing || packTicked?.length === 0}
            commitHint={uploads.uploadsMissing ? t('purchase_flow_revamp:drawer.upload_required_hint') : undefined}
            quotaNote={quotaNote}
            requiresPropertySelection={noPropertySelected}
            onSelectProperty={() => setIsPropertySelectionOpen(true)}
          />

        </Stack>
      </ResponsiveDrawer>
    </>
  )
}
